import React from 'react';
import { UserWithRole, UserIpHistory } from '../../../../../shared/services/userService';
import LoadingSpinner from '../../../../../shared/components/LoadingSpinner';

type TFn = (key: string) => string;

export function getRoleBadgeColor(roleId: number): string {
  switch (roleId) {
    case 1: return 'bg-red-500/20 text-red-400 border-red-500/50';
    case 2: return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
    case 3: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
    case 4: return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
    case 5: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    case 6: return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
  }
}

interface BasicInfoProps {
  user: UserWithRole;
  t: TFn;
}

export const BasicInfoSection: React.FC<BasicInfoProps> = ({ user, t }) => (
  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
    <h4 className="text-white font-semibold mb-4">{t('details.basicInfo')}</h4>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400">{t('details.id')}</label>
        <p className="text-white font-medium">#{user.id}</p>
      </div>
      <div>
        <label className="text-sm text-gray-400">{t('details.username')}</label>
        <p className="text-white font-medium">{user.username}</p>
      </div>
      <div>
        <label className="text-sm text-gray-400">{t('details.email')}</label>
        <p className="text-white font-medium">{user.email}</p>
      </div>
      <div>
        <label className="text-sm text-gray-400">{t('details.role')}</label>
        <div className="mt-1">
          <span className={`px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(user.roles_id)}`}>
            {user.role_name || `Rôle ${user.roles_id}`}
          </span>
          {user.role_description && (
            <p className="text-gray-400 text-xs mt-1">{user.role_description}</p>
          )}
        </div>
      </div>
      <div>
        <label className="text-sm text-gray-400">{t('details.status')}</label>
        <div className="mt-1">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            user.activated
              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
              : 'bg-red-500/20 text-red-400 border border-red-500/50'
          }`}>
            {user.activated ? t('status.active') : t('status.inactive')}
          </span>
        </div>
      </div>
      {user.last_username_change && (
        <div>
          <label className="text-sm text-gray-400">{t('details.lastUsernameChange')}</label>
          <p className="text-white font-medium">
            {new Date(user.last_username_change).toLocaleString('fr-FR')}
          </p>
        </div>
      )}
    </div>
  </div>
);

export const AccountInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
    <h4 className="text-white font-semibold mb-4">{t('details.accountInfo')}</h4>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400">{t('details.accountCreated')}</label>
        <p className="text-white font-medium">
          {user.created_at ? new Date(user.created_at).toLocaleString('fr-FR') : '-'}
        </p>
      </div>
      {user.updated_at && (
        <div>
          <label className="text-sm text-gray-400">{t('details.lastUpdated')}</label>
          <p className="text-white font-medium">
            {new Date(user.updated_at).toLocaleString('fr-FR')}
          </p>
        </div>
      )}
      <div>
        <label className="text-sm text-gray-400">{t('details.lastLogin')}</label>
        <p className="text-white font-medium">
          {user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : t('details.never')}
        </p>
      </div>
      <div>
        <label className="text-sm text-gray-400">{t('details.lastActivity')}</label>
        <p className="text-white font-medium">
          {user.last_activity ? new Date(user.last_activity).toLocaleString('fr-FR') : t('details.never')}
        </p>
      </div>
    </div>
  </div>
);

export const PremiumInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
    <h4 className="text-white font-semibold mb-4">{t('details.premiumInfo')}</h4>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400">{t('details.premium')}</label>
        <p className="text-white font-medium">
          {user.premium ? (
            <span className="text-yellow-400">{t('details.yes')}</span>
          ) : (
            <span className="text-gray-400">{t('details.no')}</span>
          )}
        </p>
      </div>
      {user.premium_type !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.premiumType')}</label>
          <p className="text-white font-medium">{user.premium_type || '-'}</p>
        </div>
      )}
      {user.premium_start && (
        <div>
          <label className="text-sm text-gray-400">{t('details.premiumStart')}</label>
          <p className="text-white font-medium">
            {new Date(user.premium_start).toLocaleDateString('fr-FR')}
          </p>
        </div>
      )}
      {user.premium_end && (
        <div>
          <label className="text-sm text-gray-400">{t('details.premiumEnd')}</label>
          <p className="text-white font-medium">
            {new Date(user.premium_end).toLocaleDateString('fr-FR')}
          </p>
        </div>
      )}
      {user.premium_until && (
        <div>
          <label className="text-sm text-gray-400">{t('details.until')}</label>
          <p className="text-white font-medium">
            {new Date(user.premium_until).toLocaleDateString('fr-FR')}
          </p>
        </div>
      )}
    </div>
  </div>
);

export const GameInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
    <h4 className="text-white font-semibold mb-4">{t('details.gameInfo')}</h4>
    <div className="grid grid-cols-2 gap-4">
      {user.xp !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.xp')}</label>
          <p className="text-white font-medium">{user.xp?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.niveau !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.level')}</label>
          <p className="text-white font-medium">{user.niveau || 1}</p>
        </div>
      )}
      {user.departement_code && (
        <div>
          <label className="text-sm text-gray-400">{t('details.department')}</label>
          <p className="text-white font-medium">
            {user.department || user.departement_code}
          </p>
        </div>
      )}
      {user.argents !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.money')}</label>
          <p className="text-white font-medium">{user.argents?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.medailles !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.medals')}</label>
          <p className="text-white font-medium">{user.medailles?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.batiments !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.buildings')}</label>
          <p className="text-white font-medium">{user.batiments || 0}</p>
        </div>
      )}
      {user.vehicules !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.vehicles')}</label>
          <p className="text-white font-medium">{user.vehicules || 0}</p>
        </div>
      )}
    </div>
    {user.statistiques && (
      <div className="mt-4 pt-4 border-t border-gray-700">
        <h5 className="text-white font-medium mb-3">{t('details.statistics')}</h5>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400">{t('details.statBuildings')}</label>
            <p className="text-white font-medium">{user.statistiques.batiments || 0}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">{t('details.statVehicles')}</label>
            <p className="text-white font-medium">{user.statistiques.vehicules || 0}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">{t('details.statInterventions')}</label>
            <p className="text-white font-medium">{user.statistiques.interventions || 0}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">{t('details.statTickets')}</label>
            <p className="text-white font-medium">{user.statistiques.tickets_support || 0}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">{t('details.statFriends')}</label>
            <p className="text-white font-medium">{user.statistiques.amis || 0}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">{t('details.statPersonnel')}</label>
            <p className="text-white font-medium">{user.statistiques.personnel || 0}</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

interface SecurityInfoProps {
  user: UserWithRole;
  ipHistory: UserIpHistory[];
  loadingIpHistory: boolean;
  t: TFn;
}

export const SecurityInfoSection: React.FC<SecurityInfoProps> = ({ user, ipHistory, loadingIpHistory, t }) => (
  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
    <h4 className="text-white font-semibold mb-4">{t('details.securityInfo')}</h4>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400">{t('details.currentIp')}</label>
        <p className="text-white font-medium">
          {user.last_ip || (
            <span className="text-gray-500">{t('details.notAvailable')}</span>
          )}
        </p>
      </div>
      {user.failed_attempts !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.failedAttempts')}</label>
          <p className="text-white font-medium">{user.failed_attempts || 0}</p>
        </div>
      )}
      {user.last_failed_attempt && (
        <div>
          <label className="text-sm text-gray-400">{t('details.lastFailedAttempt')}</label>
          <p className="text-white font-medium">
            {new Date(user.last_failed_attempt).toLocaleString('fr-FR')}
          </p>
        </div>
      )}
      {user.bann !== undefined && (
        <div>
          <label className="text-sm text-gray-400">{t('details.banStatus')}</label>
          <p className="text-white font-medium">
            {user.bann === 0 ? (
              <span className="text-green-400">{t('details.notBanned')}</span>
            ) : (
              <span className="text-red-400">{t('details.banned')}</span>
            )}
          </p>
        </div>
      )}
      {user.ban_end && (
        <div>
          <label className="text-sm text-gray-400">{t('details.banEnd')}</label>
          <p className="text-white font-medium">
            {new Date(user.ban_end).toLocaleString('fr-FR')}
          </p>
        </div>
      )}
      {user.ban_reason && (
        <div className="col-span-2">
          <label className="text-sm text-gray-400">{t('details.banReason')}</label>
          <p className="text-white font-medium">{user.ban_reason}</p>
        </div>
      )}
      {user.muted && (
        <div>
          <label className="text-sm text-gray-400">{t('details.muted')}</label>
          <p className="text-white font-medium">
            {user.muted ? (
              <span className="text-red-400">{t('details.yes')}</span>
            ) : (
              <span className="text-green-400">{t('details.no')}</span>
            )}
          </p>
        </div>
      )}
      {user.mute_end && (
        <div>
          <label className="text-sm text-gray-400">{t('details.muteEnd')}</label>
          <p className="text-white font-medium">
            {new Date(user.mute_end).toLocaleString('fr-FR')}
          </p>
        </div>
      )}
      {user.mute_reason && (
        <div className="col-span-2">
          <label className="text-sm text-gray-400">{t('details.muteReason')}</label>
          <p className="text-white font-medium">{user.mute_reason}</p>
        </div>
      )}
      {user.banned_ips_id && (
        <div>
          <label className="text-sm text-gray-400">{t('details.bannedIpId')}</label>
          <p className="text-white font-medium">#{user.banned_ips_id}</p>
        </div>
      )}
    </div>

    <div className="mt-6 pt-4 border-t border-gray-700">
      <h5 className="text-white font-medium mb-3">{t('details.ipHistory')}</h5>
      {loadingIpHistory ? (
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      ) : ipHistory.length > 0 ? (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {ipHistory.map((ipRecord) => (
            <div
              key={ipRecord.id}
              className={`p-3 rounded-lg border ${
                ipRecord.ip_address === user.last_ip
                  ? 'bg-blue-500/10 border-blue-500/50'
                  : 'bg-gray-700/30 border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-white font-medium">
                    {ipRecord.ip_address}
                    {ipRecord.ip_address === user.last_ip && (
                      <span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/50">
                        {t('details.current')}
                      </span>
                    )}
                  </p>
                  <div className="flex gap-4 mt-1 text-xs text-gray-400">
                    <span>
                      {t('details.firstSeen')}: {new Date(ipRecord.first_seen).toLocaleString('fr-FR')}
                    </span>
                    <span>
                      {t('details.lastSeen')}: {new Date(ipRecord.last_seen).toLocaleString('fr-FR')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm">{t('details.noIpHistory')}</p>
      )}
    </div>
  </div>
);

export const PreferencesSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => {
  const hasPrefs =
    user.email_notifications !== undefined ||
    user.push_notifications !== undefined ||
    user.newsletter !== undefined ||
    user.profile_visibility !== undefined ||
    user.show_email !== undefined ||
    user.show_stats !== undefined;

  if (!hasPrefs) return null;

  return (
    <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
      <h4 className="text-white font-semibold mb-4">{t('details.preferences')}</h4>
      <div className="grid grid-cols-2 gap-4">
        {user.email_notifications !== undefined && (
          <div>
            <label className="text-sm text-gray-400">{t('details.emailNotifications')}</label>
            <p className="text-white font-medium">
              {user.email_notifications ? (
                <span className="text-green-400">{t('details.enabled')}</span>
              ) : (
                <span className="text-gray-400">{t('details.disabled')}</span>
              )}
            </p>
          </div>
        )}
        {user.push_notifications !== undefined && (
          <div>
            <label className="text-sm text-gray-400">{t('details.pushNotifications')}</label>
            <p className="text-white font-medium">
              {user.push_notifications ? (
                <span className="text-green-400">{t('details.enabled')}</span>
              ) : (
                <span className="text-gray-400">{t('details.disabled')}</span>
              )}
            </p>
          </div>
        )}
        {user.newsletter !== undefined && (
          <div>
            <label className="text-sm text-gray-400">{t('details.newsletter')}</label>
            <p className="text-white font-medium">
              {user.newsletter ? (
                <span className="text-green-400">{t('details.yes')}</span>
              ) : (
                <span className="text-gray-400">{t('details.no')}</span>
              )}
            </p>
          </div>
        )}
        {user.profile_visibility && (
          <div>
            <label className="text-sm text-gray-400">{t('details.profileVisibility')}</label>
            <p className="text-white font-medium">{user.profile_visibility}</p>
          </div>
        )}
        {user.show_email !== undefined && (
          <div>
            <label className="text-sm text-gray-400">{t('details.showEmail')}</label>
            <p className="text-white font-medium">
              {user.show_email ? (
                <span className="text-green-400">{t('details.yes')}</span>
              ) : (
                <span className="text-gray-400">{t('details.no')}</span>
              )}
            </p>
          </div>
        )}
        {user.show_stats !== undefined && (
          <div>
            <label className="text-sm text-gray-400">{t('details.showStats')}</label>
            <p className="text-white font-medium">
              {user.show_stats ? (
                <span className="text-green-400">{t('details.yes')}</span>
              ) : (
                <span className="text-gray-400">{t('details.no')}</span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
